import LocationHandler from './location-handler';
import { getUnitsByFeatures } from './units';
import { UnitsVisibility } from '../types/units-visibility';
import Popup from './popup';

/**
 * UnitsManager - Hanterar marker visibility och rendering
 * 
 * ANSVAR:
 * - Hantera visibility states (ALL, AREA, HIDE)
 * - Renderera markers baserat på visibility och selected areas
 * - Hantera popup när markers klickas
 * 
 * INTE ANSVAR FÖR:
 * - React component state
 * - Units data fetching
 * - Filter logic
 */
class UnitsManager {
    constructor() {
        this.popup = null;
        this.renderMarkersTimeout = null;
        this.mapIdleListener = null;
        this.pendingRenderRequest = null;
        this.pendingRenderResolvers = [];
        this.renderMarkersThrottleMs = 200;
    }

    /**
     * Sätt popup-instans (för att kunna ta bort den vid behov)
     * @param {Object} popup - Popup instance
     */
    setPopup(popup) {
        this.popup = popup;
    }

    /**
     * Renderera units baserat på visibility mode
     * @param {string} unitsVisible - Visibility mode (ALL, AREA, HIDE)
     * @param {Array} allUnits - Alla units
     * @param {Array} selectedAreas - Valda areas (GeoJSON features)
     * @param {Object} mapState - MapStateManager instance
     * @param {Object} fetch - Fetch function för popup
     * @param {Object} salesDistrictFilters - Optional: Filter object med unitsIncludeExclude
     * @param {Array} allUnitsOriginal - Optional: Original allUnits array för att hitta inkluderade units
     */
    async renderUnits(unitsVisible, allUnits, selectedAreas, mapState, fetch, salesDistrictFilters = null, allUnitsOriginal = null) {
        const map = mapState.getMap();
        const maps = mapState.getMaps();

        if (!map || !maps) {
            console.warn('UnitsManager: Map not initialized');
            return;
        }

        if (unitsVisible === UnitsVisibility.AREA) {
            // Visa endast units i valda areas
            let filteredUnits = getUnitsByFeatures(selectedAreas, allUnits);
            
            // Lägg till inkluderade units från filtret även om de ligger utanför valda områden
            if (salesDistrictFilters && salesDistrictFilters.unitsIncludeExclude && salesDistrictFilters.unitsIncludeExclude.length > 0) {
                const included = salesDistrictFilters.unitsIncludeExclude.filter(inc => inc.include);
                if (included.length > 0) {
                    const unitsToSearch = allUnitsOriginal || allUnits;
                    included.forEach(inc => {
                        const unit = unitsToSearch.find(u => u.delfiId === inc.delfiId);
                        if (unit && !filteredUnits.find(u => u.delfiId === unit.delfiId)) {
                            // Lägg till unit om den inte redan finns i filteredUnits
                            filteredUnits.push(unit);
                        }
                    });
                }
            }
            
            await this.renderMarkers(filteredUnits, map, maps, fetch);
        } else if (unitsVisible === UnitsVisibility.ALL) {
            // Visa alla units
            await this.renderMarkers(allUnits, map, maps, fetch);
        } else {
            // Dölj alla markers
            this.hideMarkers();
        }
    }

    /**
     * Renderera markers för en lista av units
     * @param {Array} unitsToShow - Units att visa
     * @param {Object} map - Google Maps map instance
     * @param {Object} maps - Google Maps API instance
     * @param {Object} fetch - Fetch function för popup
     */
    async renderMarkers(unitsToShow, map, maps, fetch) {
        if (!map || !maps) {
            console.warn('UnitsManager: Map not initialized in renderMarkers');
            return;
        }

        return new Promise((resolve, reject) => {
            this.pendingRenderRequest = {
                unitsToShow,
                map,
                maps,
                fetch
            };

            this.pendingRenderResolvers.push({ resolve, reject });

            if (this.renderMarkersTimeout) {
                clearTimeout(this.renderMarkersTimeout);
            }

            if (this.mapIdleListener) {
                maps.event.removeListener(this.mapIdleListener);
                this.mapIdleListener = null;
            }

            this.mapIdleListener = maps.event.addListenerOnce(map, 'idle', () => {
                this.mapIdleListener = null;
                this.flushRenderMarkersQueue();
            });

            // Fallback om idle inte triggas (t.ex. initial render)
            this.renderMarkersTimeout = setTimeout(() => {
                if (this.mapIdleListener) {
                    maps.event.removeListener(this.mapIdleListener);
                    this.mapIdleListener = null;
                }
                this.flushRenderMarkersQueue();
            }, this.renderMarkersThrottleMs);
        });
    }

    async flushRenderMarkersQueue() {
        const request = this.pendingRenderRequest;
        const resolvers = this.pendingRenderResolvers.splice(0);

        this.pendingRenderRequest = null;
        this.renderMarkersTimeout = null;

        if (!request) {
            resolvers.forEach(({ resolve }) => resolve());
            return;
        }

        try {
            await this.performRenderMarkers(request);
            resolvers.forEach(({ resolve }) => resolve());
        } catch (error) {
            console.error('UnitsManager: Failed to render markers', error);
            resolvers.forEach(({ reject }) => reject(error));
        }
    }

    async performRenderMarkers({ unitsToShow, map, maps, fetch }) {
        // Ta bort befintliga markers innan vi renderar nya
        LocationHandler.removeMarkers();

        if (!unitsToShow || unitsToShow.length === 0) {
            return;
        }

        await LocationHandler.createLocations({
            map,
            maps,
            units: unitsToShow,
            disableFitBounds: true, // Alltid disable fit bounds under filter-ändringar
            onClick: ({ units: loadedUnits }) => {
                this.handleMarkerClick(loadedUnits, map, maps, fetch);
            }
        });
    }

    /**
     * Hantera marker click - visa popup
     * @param {Array} units - Units att visa i popup
     * @param {Object} map - Google Maps map instance
     * @param {Object} maps - Google Maps API instance
     * @param {Object} fetch - Fetch function för popup
     */
    handleMarkerClick(units, map, maps, fetch) {
        // Ta bort befintlig popup om den finns
        if (this.popup) {
            this.popup.remove();
            this.popup = null;
        }

        // Skapa ny popup
        this.popup = Popup({
            fetch,
            maps,
            units
        });

        this.popup.setMap(map);
    }

    /**
     * Dölj alla markers
     */
    hideMarkers() {
        if (this.renderMarkersTimeout) {
            clearTimeout(this.renderMarkersTimeout);
            this.renderMarkersTimeout = null;
        }

        if (this.mapIdleListener && this.pendingRenderRequest && this.pendingRenderRequest.maps) {
            this.pendingRenderRequest.maps.event.removeListener(this.mapIdleListener);
            this.mapIdleListener = null;
        }

        if (this.pendingRenderResolvers.length) {
            this.pendingRenderResolvers.forEach(({ resolve }) => resolve());
            this.pendingRenderResolvers = [];
        }

        this.pendingRenderRequest = null;
        LocationHandler.removeMarkers();
    }

    /**
     * Uppdatera markers när selected areas ändras (om vi är i AREA mode)
     * @param {string} unitsVisible - Nuvarande visibility mode
     * @param {Array} selectedAreas - Nya selected areas
     * @param {Array} allUnits - Alla units
     * @param {Object} mapState - MapStateManager instance
     * @param {Object} fetch - Fetch function för popup
     * @param {Object} salesDistrictFilters - Optional: Filter object med unitsIncludeExclude
     * @param {Array} allUnitsOriginal - Optional: Original allUnits array för att hitta inkluderade units
     */
    async updateMarkersForSelectedAreas(unitsVisible, selectedAreas, allUnits, mapState, fetch, salesDistrictFilters = null, allUnitsOriginal = null) {
        if (unitsVisible === UnitsVisibility.AREA) {
            let filteredUnits = getUnitsByFeatures(selectedAreas, allUnits);
            
            // Lägg till inkluderade units från filtret även om de ligger utanför valda områden
            if (salesDistrictFilters && salesDistrictFilters.unitsIncludeExclude && salesDistrictFilters.unitsIncludeExclude.length > 0) {
                const included = salesDistrictFilters.unitsIncludeExclude.filter(inc => inc.include);
                if (included.length > 0) {
                    const unitsToSearch = allUnitsOriginal || allUnits;
                    included.forEach(inc => {
                        const unit = unitsToSearch.find(u => u.delfiId === inc.delfiId);
                        if (unit && !filteredUnits.find(u => u.delfiId === unit.delfiId)) {
                            // Lägg till unit om den inte redan finns i filteredUnits
                            filteredUnits.push(unit);
                        }
                    });
                }
            }
            
            await this.renderMarkers(
                filteredUnits,
                mapState.getMap(),
                mapState.getMaps(),
                fetch
            );
        }
    }
}

const unitsManagerInstance = new UnitsManager();
export default unitsManagerInstance;

